import type { ReactNode } from 'react';
import { STAGE_NAMES } from '@erectus/shared';

/**
 * "Erectus" — the evolution character, five stages (docs/PRODUCT.md §5).
 *
 * Drawn from scratch as inline SVG: an original stick-figure silhouette that
 * straightens up as the stage rises. No third-party character is referenced,
 * traced or imitated.
 */
export type Stage = 1 | 2 | 3 | 4 | 5;

interface StageGeometry {
  /** Trunk lean from vertical, degrees. */
  lean: number;
  /** Knee bend: 0 = upright legs, 1 = deep crouch. */
  crouch: number;
  /** Neck extension: 0 = head far forward, 1 = head stacked. */
  neck: number;
}

const GEOMETRY: Record<Stage, StageGeometry> = {
  1: { lean: 52, crouch: 0.95, neck: 0.1 },
  2: { lean: 34, crouch: 0.6, neck: 0.35 },
  3: { lean: 20, crouch: 0.35, neck: 0.55 },
  4: { lean: 8, crouch: 0.12, neck: 0.8 },
  5: { lean: 0, crouch: 0, neck: 1 },
};

export function Character({ stage, size = 132 }: { stage: Stage; size?: number }): ReactNode {
  const g = GEOMETRY[stage];
  const rad = (g.lean * Math.PI) / 180;

  const hip = { x: 60, y: 108 - g.crouch * 10 };
  const torso = 40;
  const shoulder = {
    x: hip.x + Math.sin(rad) * torso,
    y: hip.y - Math.cos(rad) * torso,
  };
  const neckLen = 12;
  const head = {
    x: shoulder.x + Math.sin(rad) * neckLen * (1 - g.neck) * 2.2 + 1,
    y: shoulder.y - neckLen * (0.45 + g.neck * 0.55),
  };
  const knee = { x: hip.x - 4 + g.crouch * 12, y: hip.y + 20 + g.crouch * 4 };
  const foot = { x: knee.x - g.crouch * 6, y: 146 };
  const hand = {
    x: shoulder.x + 6 + g.crouch * 14,
    y: shoulder.y + 26 + g.crouch * 24,
  };

  const color = stage >= 4 ? 'var(--color-state-good)' : stage >= 2 ? 'var(--color-state-fhp)' : 'var(--color-state-slouch)';

  return (
    <svg
      viewBox="0 0 120 160"
      width={size}
      height={size * (160 / 120)}
      role="img"
      aria-label={`Stage ${stage} of 5: ${STAGE_NAMES[stage]}`}
    >
      <ellipse cx="60" cy="150" rx="34" ry="5" fill="var(--color-line)" />
      <g stroke={color} strokeWidth="6" strokeLinecap="round" fill="none">
        {/* spine */}
        <line x1={hip.x} y1={hip.y} x2={shoulder.x} y2={shoulder.y} />
        {/* neck */}
        <line x1={shoulder.x} y1={shoulder.y} x2={head.x} y2={head.y + 7} />
        {/* arm */}
        <line x1={shoulder.x} y1={shoulder.y + 3} x2={hand.x} y2={hand.y} />
        {/* leg */}
        <line x1={hip.x} y1={hip.y} x2={knee.x} y2={knee.y} />
        <line x1={knee.x} y1={knee.y} x2={foot.x} y2={foot.y} />
      </g>
      <circle cx={head.x} cy={head.y} r="9" fill="none" stroke={color} strokeWidth="6" />
    </svg>
  );
}

export function StageStrip({ current }: { current: Stage }): ReactNode {
  return (
    <ol className="flex items-end justify-between gap-1">
      {([1, 2, 3, 4, 5] as Stage[]).map((stage) => (
        <li key={stage} className="flex flex-col items-center gap-1">
          <div className={stage === current ? '' : 'opacity-30'}>
            <Character stage={stage} size={stage === current ? 56 : 40} />
          </div>
          <span
            className={`text-[10px] ${stage === current ? 'font-semibold text-ink' : 'text-muted'}`}
          >
            {STAGE_NAMES[stage]}
          </span>
        </li>
      ))}
    </ol>
  );
}
